import {
  IHealthGroupTransition,
  IHealthTransition
} from '../entities/health_status';
import {
  ITestDocument,
  ITestDocumentForm,
  IVaccinationDocument,
  IVaccinationDocumentForm
} from '../entities/vaccine';
import {
  PostTestsDoc,
  PostVaccinationsDoc,
  ResGetHealthDocuments,
  ResGetHealthTestsDocument,
  ResGetHealthVaccinationsDocument,
  ResGetHealthVaccines,
  ResPostHealthScanTest,
  ResPostHealthScanVaccination
} from '../types/api-types';
import { BaseOrganizationEndpoints } from './base';

export class Health extends BaseOrganizationEndpoints {
  async scanVaccination(officeId: string, contents: string) {
    return this.requestWithStatus<ResPostHealthScanVaccination>(
      'POST',
      `/offices/${officeId}/vaccinations/scan`,
      {
        contents
      }
    );
  }

  async scanTest(officeId: string, contents: string) {
    return this.requestWithStatus<ResPostHealthScanTest>(
      'POST',
      `/offices/${officeId}/tests/scan`,
      { contents }
    );
  }

  async vaccines(officeId?: string) {
    return officeId
      ? this.requestWithStatus<ResGetHealthVaccines>(
          'GET',
          `/offices/${officeId}/vaccines`
        )
      : this.requestWithStatus<ResGetHealthVaccines>('GET', `/vaccines`);
  }

  async documents(options: {
    cursor?: string | null;
    search?: string | null;
    users?: string[] | null;
    status?: 'accepted' | 'pending' | 'rejected' | null;
    type?: 'test' | 'vaccination' | null | undefined;
    offices?: string[] | null;
    limit?: number | null;
  }) {
    const {
      cursor = null,
      search = null,
      status = null,
      type = null,
      offices = null,
      limit = 50,
      users = []
    } = options;
    const qs = new URLSearchParams();
    if (cursor) {
      qs.set('cursor', cursor);
    }
    if (status) {
      qs.set('status', status);
    }
    if (search) {
      qs.set('search', search);
    }
    if (limit) {
      qs.set('limit', limit.toString());
    }
    if (type) {
      qs.set('document', type);
    }

    if (users && users.length > 0) {
      users.forEach((p) => qs.append('user', p));
    }
    if (offices && offices.length > 0) {
      offices.forEach((p) => qs.append('office', p));
    }
    const params = qs.toString();

    return this.requestWithStatus<ResGetHealthDocuments>(
      'GET',
      `/documents?${params}`
    );
  }

  async getTestDocuments(userId: string) {
    return this.requestWithStatus<ResGetHealthTestsDocument>(
      'GET',
      `/tests?user=${userId}`
    );
  }

  async getVaccinationDocuments(userId: string) {
    return this.requestWithStatus<ResGetHealthVaccinationsDocument>(
      'GET',
      `/vaccinations?user=${userId}`
    );
  }

  async getTestDocument(id: string) {
    return this.requestWithStatus<ITestDocument>('GET', `/tests/${id}`);
  }

  async getVaccinationDocument(id: string) {
    return this.requestWithStatus<IVaccinationDocument>(
      'GET',
      `/vaccinations/${id}`
    );
  }

  async addVaccinationDocument(
    officeId: string,
    vaccine: IVaccinationDocumentForm
  ) {
    return this.requestWithStatus<PostVaccinationsDoc>(
      'POST',
      `/offices/${officeId}/vaccinations`,
      vaccine
    );
  }

  async addTestDocument(officeId: string, test: ITestDocumentForm) {
    return this.requestWithStatus<PostTestsDoc>(
      'POST',
      `/offices/${officeId}/tests`,
      test
    );
  }

  async rejectDocument(
    type: 'vaccinations' | 'tests',
    doc: { id: string },
    body: { reason: 'image' | 'data' | 'other'; comments: string }
  ) {
    return this.requestWithStatus<PostVaccinationsDoc>(
      'PUT',
      `/${type}/${doc.id}/respond/rejected`,
      body
    );
  }

  async acceptDocument(
    type: 'vaccinations' | 'tests',
    doc: { id: string },
    body: {}
  ) {
    return this.requestWithStatus<PostVaccinationsDoc>(
      'PUT',
      `/${type}/${doc.id}/respond/accepted`,
      body
    );
  }

  async changeUserHealth(user: { id: string }, body: IHealthTransition) {
    return this.requestWithStatus<unknown>(
      'PUT',
      `/users/${user.id}/health/status`,
      body
    );
  }

  async changeUserGroupHealth(
    user: { id: string },
    body: IHealthGroupTransition
  ) {
    return this.requestWithStatus<unknown>(
      'PUT',
      `/users/${user.id}/health/group`,
      body
    );
  }
}
